<template>
  <PmDropzonePure v-slot="slotProps" :type="dragAndDropType!" @drop="onDrop">
    <div
      :class="[componentClass.root, classes, slotProps.classes]"
      no-data-resource-id="resource.id"
      no-data-resource-type-id="resource.resourceFunction.resourceType.id"
    >
      <div v-if="selectVisible" :class="componentClass.element('select')">
        <PmCheckboxPure
          size="small"
          :disabled="!canBeSelected"
          :value="isSelected"
          @input="onSelectInput"
        />
      </div>

      <PmResourceShortPure
        v-if="shortText"
        :class="componentClass.element('short')"
        :icon="icon"
        :label="shortText"
        :quantity-needed="quantityNeeded"
        :quantity-achieved="quantityAchieved"
        :quantity-unclear="quantityUnclear"
        :is-locked="props.isLocked"
        :note="props.note"
        @click="emit('showDetails')"
        @open-note="emit('showDetails')"
      />

      <div v-if="hasActions" :class="componentClass.element('actions')">
        <PmResourceActionPure
          v-show="hasSearchButton"
          :class="componentClass.element('action')"
          icon="search.small"
          @click="emit('search')"
        />
      </div>

      <div :class="componentClass.element('allocations')">
        <slot v-bind="{ classes: componentClass.element('allocation') }" />
      </div>

      <div
        v-if="isTimeVisible"
        :class="componentClass.element('time')"
        :title="timeDetails"
      >
        {{ time }}
      </div>
    </div>
  </PmDropzonePure>
</template>

<script setup lang="ts">
import { computed, type VNode } from 'vue'
import { useComponentClass } from '@thomasaull-shared/composables'
import { type ResourceType, type DragAndDropType } from '@/constants/persoplan'
import { type Icon } from '@/constants/icons'
import { startEndDate } from '@/utilities/date'
import PmCheckboxPure from '@/components/basics/PmCheckboxPure.vue'
import PmResourceActionPure from '@/components/persoplan/PmResourceActionPure.vue'
import PmDropzonePure, {
  type Emits as EmitsDropzonePure,
} from '@/components/utilities/PmDropzonePure.vue'
import PmResourceShortPure from '@/components/persoplan/PmResource/PmResourceShortPure.vue'
import { isNil } from 'lodash-es'
import type { Nilable } from '../../../types/misc'
import { height as heightOfResourceAllocation } from '../PmResourceAllocation/PmResourceAllocationPure.vue'

export interface Props {
  shortText?: string
  selectVisible?: boolean
  canBeSelected?: boolean
  isSelected?: boolean
  hasSearchButton?: boolean
  startDate: Date
  endDate: Date
  icon?: Icon
  type: ResourceType
  quantityNeeded?: number
  quantityAchieved?: number
  quantityUnclear?: number
  isLocked?: boolean
  isTimeVisible?: boolean
  note?: Nilable<string>
  numberOfResourceAllocations?: number
}

const props = withDefaults(defineProps<Props>(), {
  canBeSelected: true,
  quantityNeeded: 1,
  isTimeVisible: true,
  // note: 'Blupp 123',
  numberOfResourceAllocations: 0,
})

export type Emits = {
  showDetails: []
  search: []
  createResourceAllocation: [payload: { id: number; resourceType: string }]
  select: []
  unselect: []
}

const emit = defineEmits<Emits>()

export type Slots = {
  default: (props: { classes: string }) => VNode[]
}

const slots = defineSlots<Slots>()

const componentClass = useComponentClass()

const allowedDragAndDropTypes: DragAndDropType[] = [
  'createResourceAllocationAddress',
  'createResourceAllocationVehicle',
]

const classes = computed(() => {
  return {
    'is-selected': props.isSelected,
    'is-danger': isDanger.value,
  }
})

const time = computed(() => {
  return startEndDate(props.startDate, props.endDate)
})

const timeDetails = computed(() => {
  return startEndDate(props.startDate, props.endDate, {
    showDateAnyway: true,
    showFull: true,
  })
})

const dragAndDropType = computed<DragAndDropType>(() => {
  if (props.type === 'address' || props.type === 'freelancer')
    return 'createResourceAllocationAddress'

  if (props.type === 'vehicle') return 'createResourceAllocationVehicle'

  throw new Error('dragAndDropType is undefined')
})

const isDanger = computed(() => {
  if (!props.quantityNeeded) return false
  if ((props.quantityAchieved ?? 0) < props.quantityNeeded) return true

  return false
})

const hasActions = computed(() => {
  if (props.hasSearchButton) return true
  return false
})

const hasInfo = computed(() => {
  if (props.isLocked) return true
  return false
})

function onDrop(payload: EmitsDropzonePure['drop']) {
  if (!allowedDragAndDropTypes.includes(payload.type)) {
    throw new Error('Not an allowed drag and drop type')
  }

  emit('createResourceAllocation', {
    id: payload.data.id,
    resourceType: payload.data.resourceType,
  })
}

function onSelectInput(selected: boolean) {
  if (selected) emit('select')
  else emit('unselect')
}

const allocationsHeight = computed(() => {
  const listGap = 2

  // We need to sutract the last listGap since it will not be used in a flex container
  return `${
    props.numberOfResourceAllocations * (heightOfResourceAllocation + listGap) -
    listGap
  }px`
})
</script>

<style lang="scss">
.PmResourcePure {
  $block: &;

  display: flex;
  align-items: flex-start;
  margin-bottom: 2px;
  margin-left: 8px;
  margin-right: 8px;
  border-radius: constant.$borderRadius-default;
  position: relative;
  font-size: constant.$fontSize-default;
  min-height: 17px;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    // Highglihted/Dragged over/Danger styles
    $spread: 2px;

    content: '';
    position: absolute;
    top: -$spread;
    left: -$spread;
    bottom: -$spread;
    right: -$spread;
    background-color: rgba(color.$primary-500, 0.25);
    border: 2px solid color.$primary-500;
    border-radius: constant.$borderRadius-default + $spread;
    opacity: 0;
    transition: opacity constant.$duration-fast;
    pointer-events: none;

    #{$block}.is-draggedOver & {
      outline: 2px solid fuchsia;
    }
  }

  &.is-draggedOver {
    &::before {
      opacity: 1;
    }
  }

  &.is-danger:not(#{$block}.is-draggedOver) {
    &::before {
      opacity: 1;
      background-color: color.$danger-200;
      border-color: color.$danger-500;
    }
  }

  &-select {
    margin-right: 2px;
    position: relative;
  }

  &-short {
    flex-basis: auto;
    flex-shrink: 0;
    justify-self: start;
    align-self: flex-start;
    min-height: 17px;

    #{$block}.is-draggedOver & {
      @include cssVar.define(
        '.PmResourceShortPure',
        'colorBackground',
        color.$primary-500
      );
    }

    /* stylelint-disable */
    .PmAppointmentPure:hover & {
      width: max-content;
    }
    /* stylelint-enable */
  }

  &-actions {
    display: flex;
    position: relative;
    gap: 2px;
    margin-left: 2px;
  }

  &-allocations {
    position: relative;
    padding-left: 4px;
    padding-right: 2px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex-grow: 1;
    // Comment in for lazyloaded resource allocations
    // height: v-bind('allocationsHeight');
  }

  &-allocation {
    // Empty
  }

  &-time {
    @include mixin.truncateText;
    @include mixin.dateAndTime;

    flex-basis: auto;
    padding-top: 1px;
    padding-bottom: 1px;
    margin-left: 2px;
    margin-right: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    position: relative;
    flex-shrink: 0;

    // TODO: Use Container Queries
    // #{$block}--compact & {
    //   flex-shrink: 1;
    // }
  }
}
</style>
